// src/pages/Settings.js
import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import toast from 'react-hot-toast';
import { User, Lock, Save, Eye, EyeOff, Building2, ImagePlus } from 'lucide-react';

export default function Settings() {
  const { user, isAdmin }              = useAuth();
  const { businessName, businessTagline, logoUrl, reload } = useSettings();

  // Business settings
  const [biz, setBiz]         = useState({ business_name:'', business_tagline:'', logo_url:'' });
  const [bizLoading, setBizLoading] = useState(false);
  const fileRef = useRef();

  // Profile
  const [profile, setProfile] = useState({ username:'', email:'' });
  const [profileLoading, setProfileLoading] = useState(false);

  // Password
  const [pwd, setPwd]         = useState({ old_password:'', new_password:'', confirm_password:'' });
  const [pwdLoading, setPwdLoading]   = useState(false);
  const [showOld,    setShowOld]      = useState(false);
  const [showNew,    setShowNew]      = useState(false);
  const [showConfirm,setShowConfirm]  = useState(false);

  useEffect(() => {
    setBiz({ business_name: businessName, business_tagline: businessTagline, logo_url: logoUrl });
  }, [businessName, businessTagline, logoUrl]);

  useEffect(() => {
    api.get('/auth?action=profile')
      .then(res => setProfile({ username: res.data.username, email: res.data.email }))
      .catch(() => { if (user) setProfile({ username: user.username, email: user.email }); });
  }, [user]);

  // Logo file → base64
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error('Logo must be under 500KB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setBiz(b => ({ ...b, logo_url: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const saveBiz = async (e) => {
    e.preventDefault();
    if (!biz.business_name.trim()) return toast.error('Business name required');
    setBizLoading(true);
    try {
      await api.post('/settings', biz);
      await reload();
      toast.success('Business settings saved!');
    } catch (e) { toast.error(e.response?.data?.error || 'Error saving settings'); }
    finally { setBizLoading(false); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!profile.username.trim()) return toast.error('Username required');
    if (!profile.email.trim())    return toast.error('Email required');
    setProfileLoading(true);
    try {
      const res = await api.post('/auth?action=update-profile', profile);
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      toast.success('Profile updated!');
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setProfileLoading(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (!pwd.old_password)           return toast.error('Enter current password');
    if (pwd.new_password.length < 6) return toast.error('Min. 6 characters');
    if (pwd.new_password !== pwd.confirm_password) return toast.error('Passwords do not match');
    setPwdLoading(true);
    try {
      await api.post('/auth?action=change-password', { old_password: pwd.old_password, new_password: pwd.new_password });
      toast.success('Password changed!');
      setPwd({ old_password:'', new_password:'', confirm_password:'' });
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setPwdLoading(false); }
  };

  const strength = (p) => {
    if (!p) return { label:'', color:'#ccc', width:'0%' };
    let s = 0;
    if (p.length>=6) s++; if (p.length>=10) s++;
    if (/[A-Z]/.test(p)) s++; if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    const labels=['','Weak','Fair','Good','Strong','Very Strong'];
    const colors=['#ccc','#ef4444','#f97316','#eab308','#22c55e','#16a34a'];
    return { label:labels[s], color:colors[s], width:(s*20)+'%' };
  };
  const pwdStrength = strength(pwd.new_password);

  return (
    <div>
      <h2 className="page-title">Settings</h2>

      <div className="settings-grid" style={{ gridTemplateColumns: isAdmin ? '1fr 1fr' : '1fr 1fr' }}>

        {/* ── Business Settings (admin only) ── */}
        {isAdmin && (
          <div className="card settings-card" style={{ gridColumn: 'span 2' }}>
            <div className="settings-card-header">
              <div className="settings-card-icon" style={{ background:'#f0fdf4' }}><Building2 size={22} color="#16a34a"/></div>
              <div>
                <h3>Business Settings</h3>
                <p>Customize your club name, tagline and logo</p>
              </div>
            </div>
            <form onSubmit={saveBiz}>
              <div className="form-row">
                <div className="form-group">
                  <label>Business Name</label>
                  <input value={biz.business_name}
                    onChange={e => setBiz(b => ({...b, business_name: e.target.value}))}
                    placeholder="e.g. Champion Snooker Club"/>
                </div>
                <div className="form-group">
                  <label>Tagline</label>
                  <input value={biz.business_tagline}
                    onChange={e => setBiz(b => ({...b, business_tagline: e.target.value}))}
                    placeholder="e.g. Management System"/>
                </div>
              </div>

              {/* Logo upload */}
              <div className="form-group">
                <label>Club Logo</label>
                <div style={{ display:'flex', gap:16, alignItems:'center' }}>
                  <div style={{ width:80, height:80, border:'2px dashed #e2e8f0', borderRadius:12, display:'flex', alignItems:'center', justifyContent:'center', background:'#f8fafc', flexShrink:0, overflow:'hidden' }}>
                    {biz.logo_url
                      ? <img src={biz.logo_url} alt="logo" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                      : <span style={{ fontSize:32 }}>🎱</span>
                    }
                  </div>
                  <div>
                    <button type="button" className="btn btn-outline" onClick={() => fileRef.current.click()}>
                      <ImagePlus size={15}/> {biz.logo_url ? 'Change Logo' : 'Upload Logo'}
                    </button>
                    {biz.logo_url && (
                      <button type="button" className="btn btn-danger btn-sm" style={{ marginLeft:8 }}
                        onClick={() => setBiz(b => ({...b, logo_url:''}))}>
                        Remove
                      </button>
                    )}
                    <div style={{ fontSize:12, color:'#94a3b8', marginTop:6 }}>PNG, JPG — max 500KB. Appears in sidebar & login page.</div>
                    <input ref={fileRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handleLogoChange}/>
                  </div>
                </div>
              </div>

              <button type="submit" className="btn btn-primary" disabled={bizLoading}>
                <Save size={15}/> {bizLoading ? 'Saving…' : 'Save Business Settings'}
              </button>
            </form>
          </div>
        )}

        {/* ── Profile ── */}
        <div className="card settings-card">
          <div className="settings-card-header">
            <div className="settings-card-icon" style={{ background:'#eff6ff' }}><User size={22} color="#2563eb"/></div>
            <div>
              <h3>My Profile</h3>
              <p>Update username and email · <strong style={{ textTransform:'capitalize' }}>{user?.role}</strong></p>
            </div>
          </div>
          <form onSubmit={saveProfile}>
            <div className="form-group">
              <label>Username</label>
              <input type="text" required value={profile.username}
                onChange={e => setProfile(f => ({...f, username: e.target.value}))} placeholder="admin"/>
            </div>
            <div className="form-group">
              <label>Email Address</label>
              <input type="email" required value={profile.email}
                onChange={e => setProfile(f => ({...f, email: e.target.value}))} placeholder="admin@club.com"/>
            </div>
            <button type="submit" className="btn btn-primary" disabled={profileLoading}>
              <Save size={15}/> {profileLoading ? 'Saving…' : 'Save Profile'}
            </button>
          </form>
        </div>

        {/* ── Password ── */}
        <div className="card settings-card">
          <div className="settings-card-header">
            <div className="settings-card-icon" style={{ background:'#fef3c7' }}><Lock size={22} color="#d97706"/></div>
            <div><h3>Change Password</h3><p>Keep your account secure</p></div>
          </div>
          <form onSubmit={savePassword}>
            <div className="form-group">
              <label>Current Password</label>
              <div className="input-with-icon">
                <input type={showOld?'text':'password'} value={pwd.old_password}
                  onChange={e => setPwd(f => ({...f, old_password:e.target.value}))} placeholder="Current password"/>
                <button type="button" className="input-eye" onClick={() => setShowOld(v => !v)}>
                  {showOld ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label>New Password</label>
              <div className="input-with-icon">
                <input type={showNew?'text':'password'} value={pwd.new_password}
                  onChange={e => setPwd(f => ({...f, new_password:e.target.value}))} placeholder="Min. 6 characters"/>
                <button type="button" className="input-eye" onClick={() => setShowNew(v => !v)}>
                  {showNew ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
              {pwd.new_password && (
                <div className="pwd-strength">
                  <div className="pwd-strength-bar">
                    <div style={{ width:pwdStrength.width, background:pwdStrength.color, height:'100%', borderRadius:4, transition:'all .3s' }}/>
                  </div>
                  <span style={{ color:pwdStrength.color, fontSize:12 }}>{pwdStrength.label}</span>
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Confirm New Password</label>
              <div className="input-with-icon">
                <input type={showConfirm?'text':'password'} value={pwd.confirm_password}
                  onChange={e => setPwd(f => ({...f, confirm_password:e.target.value}))} placeholder="Repeat password"/>
                <button type="button" className="input-eye" onClick={() => setShowConfirm(v => !v)}>
                  {showConfirm ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
              {pwd.confirm_password && (
                <div style={{ fontSize:12, marginTop:4, color:pwd.new_password===pwd.confirm_password?'#22c55e':'#ef4444' }}>
                  {pwd.new_password===pwd.confirm_password ? '✓ Passwords match' : 'Passwords do not match'}
                </div>
              )}
            </div>
            <button type="submit" className="btn btn-warning" disabled={pwdLoading}>
              <Lock size={15}/> {pwdLoading ? 'Changing…' : 'Change Password'}
            </button>
          </form>
        </div>

      </div>
    </div>
  );
}
